import { FEEDBACK_CATEGORIES, type FeedbackCategory } from '@feedback-service/shared';
import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface CategorySelectProps {
  label: string;
  value?: FeedbackCategory;
  onChange: (value: FeedbackCategory) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export function CategorySelect({
  label,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  className,
}: CategorySelectProps) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'en' | 'zh-CN';

  return (
    <div className={`space-y-2 ${className || ''}`}>
      <div className="flex items-center justify-between">
        <Label htmlFor="category-select" className="text-sm font-medium">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      </div>
      <Select
        value={value ?? ''}
        onValueChange={(val) => onChange(val as FeedbackCategory)}
        disabled={disabled}
      >
        <SelectTrigger
          id="category-select"
          className={error ? 'border-destructive focus:ring-destructive' : ''}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? 'category-error' : undefined}
        >
          <SelectValue placeholder={t('form.category.placeholder')} />
        </SelectTrigger>
        <SelectContent>
          {FEEDBACK_CATEGORIES.map((category) => (
            <SelectItem key={category.value} value={category.value}>
              {category.label[currentLang] || category.label.en}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && (
        <p id="category-error" className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
